import { capitalGlobalSubStipulatorAddressFieldNames } from '@/features/proposals/components/capital-global/constants/capital-global-proposal-address-field-names.constant';
import { useCapitalGlobalProposalStep1Form } from '@/features/proposals/components/capital-global/steps/capital-global-proposal-step1-form.context';
import { CapitalGlobalProposalAddressFields } from '@/features/proposals/components/capital-global/steps/sections/capital-global-proposal-address-fields.component';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { validateEmail, validatePhone } from '@/shared/utils';
import { isCaepfDocument } from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import { Accordion, Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';

const GENDER_SELECT_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
];

type Props = {
  index: number;
};

export function CapitalGlobalProposalSubStipulantAddressRow({ index }: Props) {
  const {
    form,
    isViewMode,
    zipcodeLookupCacheRef,
    shouldShowStep1FieldErrors,
    subStipulatorCepState,
    subStipulatorErrorIndexes,
    fetchAndFillSubStipulator,
  } = useCapitalGlobalProposalStep1Form();

  const fields = capitalGlobalSubStipulatorAddressFieldNames(index);
  const itemValue = `sub-${index}`;
  const cepState = subStipulatorCepState[index] ?? {
    isLoading: false,
    hasResolved: false,
    canEditStreetNeighborhood: false,
  };

  // Começa fechado; se o "Avançar" reprovar este sub, abre sozinho — erro dentro de
  // accordion fechado é erro invisível.
  const [openValues, setOpenValues] = useState<string[]>([]);
  const hasSubmitError = subStipulatorErrorIndexes.includes(index);

  useEffect(() => {
    if (hasSubmitError) setOpenValues([itemValue]);
  }, [hasSubmitError, itemValue]);

  const fetchAndFill = useCallback(
    (rawCep: string, options?: { onlyEmpty?: boolean }) =>
      fetchAndFillSubStipulator(index, rawCep, options),
    [fetchAndFillSubStipulator, index]
  );

  // Limites de idade da Data de Nascimento (CAEPF): 18 a 99 anos — igual ao estipulante.
  const todayISODate = DateTime.now().toISODate() || '';
  const minBirthDate = DateTime.now().minus({ years: 99 }).toISODate() || '';

  return (
    <form.Subscribe
      selector={state => state.values.subStipulatorAddresses[index]}
      children={entry => {
        if (!entry) return null;

        // CAEPF (pessoa física): 14 díg. e não é empresa (CNPJ também tem 14 díg.).
        const isCaepfSub =
          !entry.isCompany && isCaepfDocument(entry.documentNumber || '');

        return (
          <Accordion.Root
            collapsible
            value={openValues}
            onValueChange={details => setOpenValues(details.value)}
          >
            <Accordion.Item
              value={itemValue}
              className='rounded-2xl border border-alba-gray-200 bg-white shadow-none'
            >
              {/* Só a identificação fica sempre visível; endereço e contato colapsam. */}
              <Accordion.ItemTrigger className='flex w-full items-center justify-between gap-4 px-5 py-4 text-left'>
                <div className='flex flex-col gap-1'>
                  <Text className='font-montserrat text-base font-semibold text-alba-black-500'>
                    {`Subestipulante ${index + 1} — ${entry.name || 'Sem razão social'}`}
                  </Text>
                  {entry.documentNumber ? (
                    <Text className='font-montserrat text-sm font-normal text-alba-black-400'>
                      {formatRegisterNumber(entry.documentNumber)}
                    </Text>
                  ) : null}
                </div>
                <Accordion.ItemIndicator>
                  <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                </Accordion.ItemIndicator>
              </Accordion.ItemTrigger>

              <Accordion.ItemContent className='border-t border-alba-gray-200'>
                <div className='flex flex-col gap-6 px-5 py-4'>
                  <CapitalGlobalProposalAddressFields
                    form={form}
                    fieldNames={fields}
                    isViewMode={isViewMode}
                    isLoadingCep={cepState.isLoading}
                    hasResolvedCep={cepState.hasResolved}
                    canEditStreetNeighborhood={cepState.canEditStreetNeighborhood}
                    zipcodeLookupCacheRef={zipcodeLookupCacheRef}
                    fetchAndFill={fetchAndFill}
                    shouldShowStep1FieldErrors={shouldShowStep1FieldErrors}
                  />

                  {isCaepfSub ? (
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                      <form.AppField
                        name={`subStipulatorAddresses[${index}].gender` as never}
                        validators={{
                          onChange: ({ value, fieldApi }) => {
                            if (isViewMode) return undefined;
                            if (!shouldShowStep1FieldErrors(fieldApi))
                              return undefined;
                            if (!String(value || '').trim())
                              return 'Sexo é obrigatório';
                            return undefined;
                          },
                        }}
                        children={field => (
                          <field.SelectField
                            label='Sexo'
                            placeholder='Selecione'
                            required
                            options={GENDER_SELECT_OPTIONS}
                            disabled={isViewMode}
                          />
                        )}
                      />
                      <form.AppField
                        name={`subStipulatorAddresses[${index}].dateOfBirth` as never}
                        validators={{
                          onChange: ({ value, fieldApi }) => {
                            if (isViewMode) return undefined;
                            if (!shouldShowStep1FieldErrors(fieldApi))
                              return undefined;
                            const raw = String(value || '').trim();
                            if (!raw) return 'Data de nascimento é obrigatória';
                            const parsed = DateTime.fromISO(raw);
                            if (!parsed.isValid)
                              return 'Data de nascimento inválida';
                            const ageInYears = DateTime.now().diff(
                              parsed,
                              'years'
                            ).years;
                            if (ageInYears < 18 || ageInYears > 99)
                              return 'A data de nascimento não é válida!';
                            return undefined;
                          },
                        }}
                        children={field => (
                          <field.DateField
                            label='Data de Nascimento'
                            placeholder='dd/mm/aaaa'
                            required
                            minDate={minBirthDate}
                            maxDate={todayISODate}
                            disabled={isViewMode}
                          />
                        )}
                      />
                    </div>
                  ) : null}

                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                    <form.AppField
                      name={`subStipulatorAddresses[${index}].phone` as never}
                      validators={{
                        onChange: ({ value, fieldApi }) => {
                          if (isViewMode) return undefined;
                          if (!shouldShowStep1FieldErrors(fieldApi))
                            return undefined;
                          const s = String(value || '').trim();
                          if (!s) return 'Telefone é obrigatório';
                          if (!validatePhone(s)) return 'Telefone inválido';
                          return undefined;
                        },
                      }}
                      children={field => (
                        <field.TextField
                          label='Telefone'
                          placeholder='(xx) xxxxx-xxxx'
                          required
                          maxLength={FIELD_MAX_LENGTHS.phone}
                          mask='(99) 99999-9999'
                          disabled={isViewMode}
                        />
                      )}
                    />
                    <form.AppField
                      name={`subStipulatorAddresses[${index}].email` as never}
                      validators={{
                        onChange: ({ value, fieldApi }) => {
                          if (isViewMode) return undefined;
                          if (!shouldShowStep1FieldErrors(fieldApi))
                            return undefined;
                          const s = String(value || '').trim();
                          if (!s) return 'E-mail é obrigatório';
                          if (!validateEmail(s)) return 'E-mail inválido';
                          return undefined;
                        },
                      }}
                      children={field => (
                        <field.TextField
                          label='E-mail'
                          lowercase
                          placeholder='Digite o e-mail'
                          required
                          maxLength={FIELD_MAX_LENGTHS.email}
                          disabled={isViewMode}
                        />
                      )}
                    />
                  </div>
                </div>
              </Accordion.ItemContent>
            </Accordion.Item>
          </Accordion.Root>
        );
      }}
    />
  );
}
